import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['item', 'selectAll', 'hidden', 'linkHidden', 'form']

  declare itemTargets: HTMLInputElement[]
  declare hiddenTarget: HTMLInputElement
  declare linkHiddenTarget: HTMLInputElement
  declare formTarget: HTMLFormElement
  declare selectAllTarget: HTMLInputElement
  declare hasSelectAllTarget: boolean

  private selectedIds = new Set<string>()

  connect (): void {
    this.loadInitialSelection()
    this.syncHiddenField()
    this.syncSelectAllCheckbox()
  }

  removeLinkedAndSubmit (event: Event): void {
    event.preventDefault()
    event.stopPropagation()

    const id = (event.currentTarget as HTMLElement).dataset.evidenceId
    if (id == null || id === '') return

    this.selectedIds.delete(id)
    const linked = new Set(
      this.linkHiddenTarget.value.split(',').map(s => s.trim()).filter(Boolean)
    )
    linked.delete(id)
    this.linkHiddenTarget.value = Array.from(linked).join(',')

    const mainFormEvidenceIdsField = document.querySelector<HTMLInputElement>('input[name="assessments_control[evidence_ids]"]')
    if (mainFormEvidenceIdsField !== null) {
      const mainFormIds = new Set(
        mainFormEvidenceIdsField.value.split(',').map(s => s.trim()).filter(Boolean)
      )
      mainFormIds.delete(id)
      mainFormEvidenceIdsField.value = Array.from(mainFormIds).join(',')
    }

    this.syncHiddenField()
    this.formTarget.requestSubmit()
  }

  toggleAll (event: Event): void {
    const checked = (event.currentTarget as HTMLInputElement).checked

    this.itemTargets.forEach(cb => {
      if (cb.disabled) return
      cb.checked = checked
      checked ? this.selectedIds.add(cb.value) : this.selectedIds.delete(cb.value)
    })

    this.syncHiddenField()
    this.syncSelectAllCheckbox()
  }

  toggleItem (event: Event): void {
    const cb = event.currentTarget as HTMLInputElement
    cb.checked ? this.selectedIds.add(cb.value) : this.selectedIds.delete(cb.value)

    this.syncHiddenField()
    this.syncSelectAllCheckbox()
  }

  submit (): void {
    this.syncHiddenField()

    const linkedIds = new Set(
      this.linkHiddenTarget.value.split(',').map(s => s.trim()).filter(Boolean)
    )
    const selectedIds = new Set(
      this.hiddenTarget.value.split(',').map(s => s.trim()).filter(Boolean)
    )
    const combined = new Set<string>([...linkedIds, ...selectedIds])

    this.linkHiddenTarget.value = Array.from(combined).join(',')

    if (this.noneChecked()) {
      this.showValidationError()
      return
    }

    this.formTarget.requestSubmit()

    this.selectedIds.clear()
    this.syncHiddenField()
  }

  clearEvidenceValidation (): void {
    this.clearValidationOn(this.itemTargets)
    if (this.hasSelectAllTarget) this.clearValidationOn([this.selectAllTarget])
  }

  syncSelectAllCheckbox (): void {
    if (!this.hasSelectAllTarget) return

    const enabled = this.itemTargets.filter(cb => !cb.disabled)
    const checkedCount = enabled.filter(cb => cb.checked).length

    this.selectAllTarget.indeterminate =
      checkedCount > 0 && checkedCount < enabled.length
    this.selectAllTarget.checked = enabled.length > 0 && checkedCount === enabled.length
  }

  private loadInitialSelection (): void {
    this.selectedIds = new Set(
      this.hiddenTarget.value.split(',').map(s => s.trim()).filter(Boolean)
    )
    this.itemTargets.forEach(cb => { if (cb.checked) this.selectedIds.add(cb.value) })
  }

  private syncHiddenField (): void {
    this.hiddenTarget.value = Array.from(this.selectedIds).join(',')
  }

  private noneChecked (): boolean {
    return !this.itemTargets.some(cb => cb.checked && !cb.disabled)
  }

  private getSelectAllTarget (): HTMLInputElement | null {
    return this.hasSelectAllTarget ? this.selectAllTarget : null
  }

  private showValidationError (): void {
    const target =
      this.itemTargets.find(cb => !cb.disabled) ?? this.getSelectAllTarget()

    if (target == null) return

    const msg = 'Please select one of these options'
    target.required = true
    target.setCustomValidity(msg);

    (target as any).reportValidity?.()
    target.focus({ preventScroll: true })
    target.reportValidity?.()

    this.attachValidationClearOnChange(target)
  }

  private attachValidationClearOnChange (target: HTMLInputElement): void {
    const clear = (): void => this.clearEvidenceValidation()

    this.itemTargets.forEach(cb => cb.addEventListener('change', clear, { once: true }))
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.addEventListener('change', clear, { once: true })
    }
  }

  private clearValidationOn (targets: HTMLInputElement[]): void {
    targets.forEach(cb => {
      cb.required = false
      cb.setCustomValidity?.('')
    })
  }
}
